import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 2rem;
  background: #000000;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 1px;
    background: #333333;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100vw;
  }
`;

const Container = styled.div`
  max-width: 1350px;
  margin: 0 auto;
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const Square = styled.div`
  width: 15px;
  height: 15px;
  background: #ffffff;
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  color: #ffffff;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.5px;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
`;

const TestimonialCard = styled(motion.div)`
  background: #0b0b0b;
  border: 1px solid #2a2a2a;
  padding: 3.5rem 2.5rem 3rem;
  position: relative;
  min-height: 280px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  
  &::before {
    content: '"';
    position: absolute;
    top: 1.5rem;
    left: 2rem;
    font-size: 8rem;
    line-height: 1;
    color: #C8AB48;
    font-family: Arial;
  }
  
  p {
    font-size: 1.2rem;
    color: #e2e8f0;
    line-height: 1.8;
    margin: 2.5rem 0 2rem;
    position: relative;
    z-index: 1;
  }
  
  footer {
    margin-top: auto;
  }
  
  .author {
    font-weight: 700;
    color: #ffffff;
    font-size: 1.1rem;
  }
  
  .role {
    display: block;
    color: #94a3b8;
    font-size: 0.95rem;
    margin-top: 0.4rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 2rem;
    min-height: 280px;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 3rem;
`;

const Dots = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #C8AB48;
  background: ${props => props.active ? '#C8AB48' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const testimonials = [
    {
      text: "I've had the pleasure of working with Lizzy over a number of years, from both sides of job hunting and recruiting for a team. She is hugely dedicated to finding the best talent, keeping up to date in the industry and is a genuine person interested in helping people which is a rarity!",
      author: "Suzanne Foster",
      role: "Vice President of Content Marketing and Customer Experience"
    },
    {
      text: "I've worked with Start Something Different for nearly a decade, consistently relying on their headhunting experience across multiple brands. Lizzy brings a sharp commercial lens to talent sourcing, understanding business priorities, brand ID and cultural nuance with precision. Her ability to deliver high caliber, well matched candidates makes her a trusted partner, especially when engaging senior stakeholders. She doesn't just fill roles she helps build teams that drive performance.",
      author: "Laura Brookbanks",
      role: "Global HR Manager"
    },
    {
      text: "I've had the pleasure of working with Lizzy and Start Something Different since its very beginning, and I haven't looked back. Lizzy is exceptionally commercially minded, taking the time to truly understand the business, its goals, and the key stakeholders involved. She never makes assumptions—she asks the right questions and always seeks clarity. I value being able to share open, honest feedback on candidates, which Lizzy then articulates with professionalism and finesse. She is an absolute pleasure to work with Lizzy just gets it.",
      author: "Grant Castleden",
      role: "People Director"
    }
  ];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const paginate = (newDirection) => {
    const newIndex = currentIndex + newDirection;
    if (newIndex >= 0 && newIndex < testimonials.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
    }
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  return (
    <Section id="testimonials">
      <Container>
        <TitleRow>
          <Square />
          <SectionTitle>Testimonials</SectionTitle>
        </TitleRow>
        
        <CarouselContainer>
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <TestimonialCard
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
            >
              <p>{testimonials[currentIndex].text}</p>
              <footer>
                <span className="author">{testimonials[currentIndex].author}</span>
                <span className="role">{testimonials[currentIndex].role}</span>
              </footer>
            </TestimonialCard>
          </AnimatePresence>
          
          <NavigationContainer>
            <Dots>
              {testimonials.map((_, index) => (
                <Dot
                  key={index}
                  active={index === currentIndex}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </Dots>
          </NavigationContainer>
        </CarouselContainer>
      </Container>
    </Section>
  );
};

export default TestimonialsSection;